import React from 'react';
import { Box, Typography, Avatar, Paper } from '@mui/material';
import { SmartToy as BotIcon, Person as PersonIcon } from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import { TutorMessage as TutorMessageType } from '../../services/tutorService';

interface TutorMessageProps {
  message: TutorMessageType;
}

const TutorMessage: React.FC<TutorMessageProps> = ({ message }) => {
  const isUser = message.role === 'user';

  return (
    <Box
      sx={{
        display: 'flex',
        gap: 1.5,
        mb: 2,
        flexDirection: isUser ? 'row-reverse' : 'row',
      }}
    >
      <Avatar
        sx={{
          bgcolor: isUser ? 'primary.main' : 'secondary.main',
          width: 36,
          height: 36,
        }}
      >
        {isUser ? <PersonIcon fontSize="small" /> : <BotIcon fontSize="small" />}
      </Avatar>

      <Paper
        elevation={0}
        sx={{
          p: 2,
          maxWidth: '80%',
          bgcolor: isUser ? 'primary.light' : 'grey.100',
          borderRadius: 2,
          borderTopLeftRadius: isUser ? 2 : 0,
          borderTopRightRadius: isUser ? 0 : 2,
        }}
      >
        <Box
          sx={{
            '& p': { m: 0, mb: 1 },
            '& p:last-child': { mb: 0 },
            '& code': {
              bgcolor: 'grey.200',
              px: 0.5,
              borderRadius: 0.5,
              fontFamily: 'monospace',
              fontSize: '0.9em',
            },
            '& pre': {
              bgcolor: 'grey.800',
              color: 'grey.100',
              p: 1.5,
              borderRadius: 1,
              overflow: 'auto',
              fontSize: '0.85em',
            },
            '& pre code': {
              bgcolor: 'transparent',
              color: 'inherit',
            },
            '& ul, & ol': { pl: 2, my: 1 },
            '& li': { mb: 0.5 },
            '& h1, & h2, & h3, & h4': { mt: 2, mb: 1 },
            '& h1': { fontSize: '1.3em' },
            '& h2': { fontSize: '1.2em' },
            '& h3': { fontSize: '1.1em' },
            '& blockquote': {
              borderLeft: 3,
              borderColor: 'grey.300',
              pl: 2,
              ml: 0,
              fontStyle: 'italic',
              color: 'text.secondary',
            },
          }}
        >
          <ReactMarkdown>{message.content}</ReactMarkdown>
        </Box>

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          {new Date(message.timestamp).toLocaleTimeString('de-DE', {
            hour: '2-digit',
            minute: '2-digit',
          })}
        </Typography>
      </Paper>
    </Box>
  );
};

export default TutorMessage;
